import { IWidgetChannelServer } from '../channel/server';
import { normalizeWidgetChannels } from '../channel/normalizers';
import { Normalizer } from '../normalizer';

import {
  File,
  WidgetButton,
  WidgetConsentItem,
  WidgetConsents,
  WidgetContactForm,
  WidgetContactFormField,
  WidgetFont,
  IWidgetCustomization,
  WidgetHeader,
  IWidgetSubmitStatus,
  WidgetWelcomeMessage
} from './client';
import {
  FileServer,
  WidgetButtonServer,
  WidgetConsentItemServer,
  WidgetConsentsServer,
  WidgetContactFormFieldServer,
  WidgetContactFormServer,
  WidgetCustomizationServer,
  WidgetFontServer,
  WidgetFontType,
  WidgetHeaderServer,
  WidgetSubmitStatusServer,
  WidgetWelcomeMessageServer
} from './server';

export const normalizeWidgetConsentItem: Normalizer<
  WidgetConsentItemServer,
  WidgetConsentItem
> = (raw) => ({
  isEnabled: raw.is_enabled,
  required: raw.is_required,
  docUrl: raw.doc_url
});

export const normalizeWidgetConsents: Normalizer<
  WidgetConsentsServer,
  WidgetConsents
> = (raw) => ({
  personalData: normalizeWidgetConsentItem(raw.personal_data),
  mailing: normalizeWidgetConsentItem(raw.mailing)
});

export const normalizeFile: Normalizer<FileServer, File> = (raw) => ({
  id: raw._id,
  url: raw.url,
  filename: raw.filename
});

export const normalizeWidgetFont = (raw: WidgetFontServer): WidgetFont => {
  if (raw.type === WidgetFontType.custom) {
    return {
      type: WidgetFontType.custom,
      id: raw._id,
      url: raw.url,
      filename: raw.filename
    };
  }

  return {
    type: WidgetFontType.preset,
    name: raw.name
  };
};

export const normalizeWidgetHeader: Normalizer<
  WidgetHeaderServer,
  WidgetHeader
> = (raw) => ({
  type: raw.type,
  logoUrl: raw.logo_url,
  title: raw.title,
  subtitle: raw.subtitle
});

export const normalizeWidgetButton: Normalizer<
  WidgetButtonServer,
  WidgetButton
> = (raw) => ({
  iconUrl: raw.icon_url
});

export const normalizeWidgetContactFormField: Normalizer<
  WidgetContactFormFieldServer,
  WidgetContactFormField
> = (raw) => ({
  type: raw.type,
  required: raw.required,
  enabled: true
});

export const normalizeWidgetContactForm: Normalizer<
  WidgetContactFormServer,
  WidgetContactForm
> = (raw) => ({
  mode: raw.mode,
  fields: raw.fields.map(normalizeWidgetContactFormField)
});

export const normalizeWidgetWelcomeMessage: Normalizer<
  WidgetWelcomeMessageServer,
  WidgetWelcomeMessage
> = (raw) => ({
  isEnabled: raw.is_enabled,
  text: raw.text
});

export const normalizeWidgetCustomization = (
  raw: WidgetCustomizationServer,
  socialButtons: IWidgetChannelServer[]
): IWidgetCustomization => ({
  contactForm: normalizeWidgetContactForm(raw.contact_form),
  consents: normalizeWidgetConsents(raw.consents),
  theme: raw.theme,
  primaryColor: raw.primary_color,
  font: normalizeWidgetFont(raw.font),
  background: raw.background ? normalizeFile(raw.background) : null,
  header: normalizeWidgetHeader(raw.header),
  button: normalizeWidgetButton(raw.button),
  botAvatarUrl: raw.bot_avatar_url,
  channels: normalizeWidgetChannels(socialButtons),
  welcomeMessage: raw.welcome_message
    ? normalizeWidgetWelcomeMessage(raw.welcome_message)
    : { isEnabled: false, text: null }
});

export const normalizeWidgetSubmitStatus: Normalizer<
  WidgetSubmitStatusServer,
  IWidgetSubmitStatus
> = (raw) => ({
  contactForm: raw.contact_form,
  personalDataConsent: raw.personal_data_consent,
  mailingConsent: raw.mailing_consent
});
